/**
 * BuddyGroupListPage — admin/delegate view of all buddy groups for a rodeo.
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { useBuddyGroups } from '../api';
import { useBuddyGroupPermissions } from '../auth/use-buddy-group-permissions';
import { BuddyGroupTable } from '../components/BuddyGroupTable';
import { DissolveGroupDialog } from '../components/DissolveGroupDialog';
import type { BuddyGroupSummary } from '../types/buddy-group';

export default function BuddyGroupListPage() {
  const { rodeoId } = useParams<{ rodeoId: string }>();
  const navigate = useNavigate();
  const { canEdit, canDissolve } = useBuddyGroupPermissions();
  const { data, isLoading, isError } = useBuddyGroups(rodeoId!);
  const [dissolveTarget, setDissolveTarget] = useState<BuddyGroupSummary | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/rodeos/${rodeoId}`)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Rodeo
        </Button>
        <div className="text-sm text-destructive">Error loading buddy groups.</div>
      </div>
    );
  }

  const groups = data?.groups ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/rodeos/${rodeoId}`)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold">Buddy Groups</h1>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate(`/rodeos/${rodeoId}/buddy-groups/conflicts`)}>
          View Conflicts
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {groups.length} group{groups.length !== 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {groups.length === 0 ? (
            <EmptyState
              icon={Users2}
              title="No buddy groups"
              description="No buddy groups have been created for this rodeo yet."
            />
          ) : (
            <BuddyGroupTable
              groups={groups}
              onView={(g) => navigate(`/rodeos/${rodeoId}/buddy-groups/${g.id}`)}
              onEdit={(g) => navigate(`/rodeos/${rodeoId}/buddy-groups/${g.id}/edit`)}
              onDissolve={(g) => setDissolveTarget(g)}
              canEdit={canEdit}
              canDissolve={canDissolve}
            />
          )}
        </CardContent>
      </Card>

      {/* Dissolve dialog */}
      {dissolveTarget && (
        <DissolveGroupDialog
          group={dissolveTarget}
          open={!!dissolveTarget}
          onClose={() => setDissolveTarget(null)}
          onDissolved={() => {
            setDissolveTarget(null);
          }}
        />
      )}
    </div>
  );
}
